import { useEffect, useState } from 'react';
import { ItemActions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { EventSections } from '../../../lib/events/EventLogger';
import { P2GText } from '@parcel2go/core-ui';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { HasClaims, HasRefund } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import Loading from '~/components/loading/Loading';
import Labels from './Labels';
import { EditType } from '../item/EditType';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props {
  item: SmartSendItemApiModel;
  editType: EditType;
}

export default function ItemOrderLabelsPanel(props: props) {
  const [actions, setActions] = useState<ItemActions>(null);
  const { getItemActions } = useSmartSendApi();
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [loading, setLoading] = useState(true);
  const inTransit = !!props.item.TrackingSummary?.DroppedOff || !!props.item.TrackingSummary?.Collected;
  const hasClaim = HasClaims(props.item);
  const hasRefund = HasRefund(props.item);

  useEffect(() => {
    track({
      type: 'edit_item_loaded',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.BookedItems, 'order-labels-panel']),
        action: props.editType
      }
    });
  }, []);

  useEffect(() => {
    const getData = async () => {
      const o = await getItemActions(props.item.Id);
      setActions(o.success ? o.result : { Links: [] });
      setLoading(false);
    };
    getData();
  }, []);

  if (loading || !actions || !actions.Links || !props.item) {
    return <Loading />;
  }

  if (hasClaim || hasRefund) {
    return (
      <P2GText variant="body2">
        {t('LABELS_NOT_AVAILABLE', {
          defaultValue: 'Labels are not available for items with claims or refunds.'
        })}
      </P2GText>
    );
  }

  if (inTransit) {
    return (
      <P2GText variant="body2">
        {t('LABELS_NOT_AVAILABLE_IN_TRANSIT', {
          defaultValue: 'Labels are not available for items that are in transit.'
        })}
      </P2GText>
    );
  }

  return <Labels item={props.item} actions={actions} />;
}
